import React, { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { API } from '@/api'
import type { Story, StoryContent } from '@/types'
import { useToast } from '@/components/ui/use-toast'
import { 
  Heart,
  MessageSquare,
  Share2,
  Bookmark,
  ThumbsUp,
  User,
  Calendar,
  Eye,
  Sparkles,
  ArrowLeft,
  Copy,
  Check,
  Play,
  Pause,
  ArrowRight
} from 'lucide-react'
import { cn } from '@/lib/utils'

interface StoryData {
  story: Story
  contents: StoryContent[]
}

// 添加来源页面类型
type SourcePage = 'discover' | 'my-works'

const StoryDetail = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const { toast } = useToast()
  
  const [loading, setLoading] = useState(true)
  const [storyData, setStoryData] = useState<StoryData | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [isLiked, setIsLiked] = useState(false)
  const [isSaved, setIsSaved] = useState(false)
  const [isCopied, setIsCopied] = useState(false)
  const [isPlaying, setIsPlaying] = useState(false)
  const [imageSize, setImageSize] = useState<{ width: number; height: number }>({ width: 0, height: 0 })
  const [autoPlayInterval, setAutoPlayInterval] = useState<number>(3000) // 自动播放间隔，改为 3 秒
  const [audioPlaying, setAudioPlaying] = useState(false)
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const [sourcePage, setSourcePage] = useState<SourcePage>('discover')

  // 获取故事详情
  const fetchStoryDetail = async () => {
    if (!id) return
    
    setLoading(true)
    try {
      const data = await API.stories.get(id)
      setStoryData(data)
    } catch (error) {
      console.error('Failed to fetch story:', error)
      toast({
        title: '错误',
        description: '获取故事详情失败',
        variant: 'destructive'
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchStoryDetail()
  }, [id])

  // 自动播放逻辑
  useEffect(() => {
    let timer: NodeJS.Timeout | null = null;

    if (isPlaying && storyData) {
      timer = setInterval(() => {
        setCurrentPage(currentPage => {
          if (currentPage >= storyData.contents.length) {
            // 到达末尾，重新从第一页开始
            return 1;
          }
          // 正常播放下一页
          return currentPage + 1;
        });
      }, autoPlayInterval);
    }

    return () => {
      if (timer) {
        clearInterval(timer);
      }
    };
  }, [isPlaying, storyData, autoPlayInterval]);

  // 在 useEffect 中获取来源页面
  useEffect(() => {
    const source = location.state?.from as SourcePage
    if (source) {
      setSourcePage(source)
    }
  }, [location.state])

  const handleCopyPrompt = async () => {
    if (!storyData?.story.description) return
    try {
      await navigator.clipboard.writeText(storyData.story.description)
      setIsCopied(true)
      setTimeout(() => setIsCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy text:', err)
    }
  }

  // 修改翻页处理函数
  const handlePrevPage = () => {
    if (isPlaying) {
      setIsPlaying(false)
    }
    if (currentPage <= 1) return
    setCurrentPage(currentPage - 1) // 直接使用 currentPage 而不是回调函数
  }

  const handleNextPage = () => {
    if (isPlaying) {
      setIsPlaying(false)
    }
    if (!storyData || currentPage >= storyData.contents.length) return
    setCurrentPage(currentPage + 1) // 直接使用 currentPage 而不是回调函数
  }

  const handleFirstPage = () => {
    if (isPlaying) {
      setIsPlaying(false)
    }
    if (currentPage <= 1) return
    setCurrentPage(1)
  }

  const handleLastPage = () => {
    if (isPlaying) {
      setIsPlaying(false)
    }
    if (!storyData || currentPage >= storyData.contents.length) return
    setCurrentPage(storyData.contents.length)
  }

  // 处理播放/暂停按钮点击
  const handlePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  // 处理图片加载完成
  const handleImageLoad = (event: React.SyntheticEvent<HTMLImageElement>) => {
    const img = event.currentTarget
    setImageSize({
      width: img.naturalWidth,
      height: img.naturalHeight
    })
  }

  // 添加音频播放处理函数
  const handleAudioPlay = () => {
    if (!audioRef.current) return
    
    if (audioPlaying) {
      audioRef.current.pause()
    } else {
      audioRef.current.play()
    }
    setAudioPlaying(!audioPlaying)
  }

  // 在页面切换时停止音频播放
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.pause()
      setAudioPlaying(false)
    }
  }, [currentPage])

  // 在组件卸载时停止音频播放
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause()
      }
    }
  }, [])

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        加载中...
      </div>
    )
  }

  if (!storyData) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <p className="text-gray-500 mb-4">故事不存在或已被删除</p>
        <Button variant="outline" onClick={() => navigate('/discover')}>
          返回发现页
        </Button>
      </div>
    )
  }

  const { story, contents } = storyData
  const currentContent = contents.find(c => c.pageNumber === currentPage)

  return (
    <div className="w-full space-y-8">
      {/* 返回按钮 */}
      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate(sourcePage === 'my-works' ? '/my-works' : '/discover')}
        className="text-gray-500 hover:text-gray-700 flex items-center gap-1.5"
      >
        <ArrowLeft className="w-4 h-4" />
        <span>返回{sourcePage === 'my-works' ? '我的作品' : '发现'}</span>
      </Button>

      {/* 顶部信息 */}
      <Card className="p-8">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-start gap-8">
            {/* 封面图 */}
            <div className="w-1/3">
              <div className="aspect-[4/3] rounded-xl overflow-hidden">
                <img 
                  src={story.coverImageUrl} 
                  alt={story.title}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            {/* 基本信息 */}
            <div className="flex-1 space-y-6">
              <div className="flex items-start justify-between gap-4">
                <h1 className="text-2xl font-bold text-gray-800">{story.title}</h1>
                
                {/* 操作按钮组 */}
                <div className="flex items-center gap-2">
                  <Button
                    variant={isLiked ? "default" : "outline"}
                    size="sm"
                    className="h-8 px-3 flex items-center gap-1.5"
                    onClick={() => setIsLiked(!isLiked)}
                  >
                    <Heart className={`w-3.5 h-3.5 ${isLiked ? 'fill-current' : ''}`} />
                    喜欢
                  </Button>
                  <Button
                    variant={isSaved ? "default" : "outline"}
                    size="sm"
                    className="h-8 px-3 flex items-center gap-1.5"
                    onClick={() => setIsSaved(!isSaved)}
                  >
                    <Bookmark className={`w-3.5 h-3.5 ${isSaved ? 'fill-current' : ''}`} />
                    收藏
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 px-3 flex items-center gap-1.5"
                  >
                    <Share2 className="w-3.5 h-3.5" />
                    分享
                  </Button>
                </div>
              </div>

              {/* 作者和日期信息 */}
              <div className="flex items-center gap-4 text-sm text-gray-500">
                <div className="flex items-center gap-1">
                  <User className="w-4 h-4" />
                  <span>{story.authorId}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  <span>{new Date(story.createdAt).toLocaleDateString()}</span>
                </div>
              </div>

              {/* 创作提示词 */}
              <div className="bg-gray-50/80 rounded-lg p-4 relative">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2 text-primary">
                    <Sparkles className="w-4 h-4" />
                    <span className="text-sm font-medium">创作提示词</span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleCopyPrompt}
                    className={`flex items-center gap-1.5 transition-colors ${
                      isCopied ? 'text-green-500' : 'text-gray-500 hover:text-primary'
                    }`}
                  >
                    {isCopied ? (
                      <>
                        <Check className="w-3.5 h-3.5" />
                        <span className="text-xs">已复制</span>
                      </>
                    ) : (
                      <>
                        <Copy className="w-3.5 h-3.5" />
                        <span className="text-xs">复制</span>
                      </>
                    )}
                  </Button>
                </div>
                <p className="text-sm text-gray-600 line-clamp-2">
                  {story.description}
                </p>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* 详细内容 */}
      <Card className="p-8">
        <div className="max-w-3xl mx-auto space-y-8">
          {/* 故事内容 */}
          {currentContent && (
            <div className="space-y-4">
              <div className="flex flex-col max-w-3xl mx-auto">
                {/* 图片容器 */}
                <div className="overflow-hidden rounded-lg">
                  <div className="relative">
                    {/* 当前页面 */}
                    <div 
                      className="bg-black"
                      style={{
                        aspectRatio: imageSize.width && imageSize.height 
                          ? `${imageSize.width} / ${imageSize.height}`
                          : '4/3'
                      }}
                    >
                      <img 
                        src={currentContent.imageUrl}
                        alt={`Page ${currentContent.pageNumber}`}
                        className="w-full h-full object-contain"
                        onLoad={handleImageLoad}
                      />
                    </div>

                    {/* 页码显示 */}
                    <div className="absolute top-2 right-2 bg-black/50 text-white px-2 py-1 rounded text-xs backdrop-blur-sm z-10">
                      {currentPage} / {contents.length}
                    </div>

                    {/* 翻页按钮 */}
                    <div className="absolute inset-0 flex items-center justify-between px-4">
                      <Button
                        variant="ghost"
                        size="icon"
                        className={cn(
                          "bg-black/20 text-white transition-opacity",
                          currentPage <= 1 ? "opacity-30 cursor-not-allowed" : "hover:bg-black/40"
                        )}
                        onClick={handleFirstPage}
                        disabled={currentPage <= 1}
                      >
                        <ArrowLeft className="h-6 w-6" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className={cn(
                          "bg-black/20 text-white transition-opacity",
                          currentPage <= 1 ? "opacity-30 cursor-not-allowed" : "hover:bg-black/40"
                        )}
                        onClick={handlePrevPage}
                        disabled={currentPage <= 1}
                      >
                        <ArrowLeft className="h-6 w-6" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className={cn(
                          "bg-black/20 text-white transition-opacity",
                          currentPage >= contents.length ? "opacity-30 cursor-not-allowed" : "hover:bg-black/40"
                        )}
                        onClick={handleNextPage}
                        disabled={currentPage >= contents.length}
                      >
                        <ArrowRight className="h-6 w-6" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className={cn(
                          "bg-black/20 text-white transition-opacity",
                          currentPage >= contents.length ? "opacity-30 cursor-not-allowed" : "hover:bg-black/40"
                        )}
                        onClick={handleLastPage}
                        disabled={currentPage >= contents.length}
                      >
                        <ArrowRight className="h-6 w-6" />
                      </Button>
                    </div>
                  </div>
                </div>
                
                {/* 文本内容 */}
                <div className="mt-3 bg-gray-50 rounded-lg">
                  <div className="relative min-h-[120px] max-h-[120px] overflow-y-auto">
                    <div className="p-4">
                      <div className="flex gap-4">
                        <p className="flex-1 text-base leading-relaxed">
                          {currentContent.textContent}
                        </p>
                        {currentContent.audioUrl && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleAudioPlay}
                            className={cn(
                              "flex-shrink-0 flex items-center gap-1.5 h-auto py-1 px-2",
                              audioPlaying ? "text-primary" : "text-gray-500 hover:text-primary"
                            )}
                          >
                            {audioPlaying ? (
                              <>
                                <Pause className="w-3.5 h-3.5" />
                                <span className="text-xs">暂停</span>
                              </>
                            ) : (
                              <>
                                <Play className="w-3.5 h-3.5" />
                                <span className="text-xs">朗读</span>
                              </>
                            )}
                          </Button>
                        )}
                      </div>
                      <audio
                        ref={audioRef}
                        src={currentContent.audioUrl}
                        onEnded={() => setAudioPlaying(false)}
                        onError={(e) => {
                          console.error('Audio playback error:', e)
                          toast({
                            title: '错误',
                            description: '音频播放失败',
                            variant: 'destructive'
                          })
                          setAudioPlaying(false)
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* 底部控制按钮 */}
                <div className="flex justify-center space-x-4 mt-4">
                  <Button 
                    variant="outline"
                    size="sm"
                    onClick={handleFirstPage}
                    disabled={currentPage <= 1}
                    className={cn(
                      "transition-opacity",
                      currentPage <= 1 && "opacity-50"
                    )}
                  >
                    首页
                  </Button>
                  <Button 
                    variant="outline"
                    size="sm"
                    onClick={handlePrevPage}
                    disabled={currentPage <= 1}
                    className={cn(
                      "transition-opacity",
                      currentPage <= 1 && "opacity-50"
                    )}
                  >
                    上一页
                  </Button>
                  <Button 
                    variant="outline"
                    size="sm"
                    onClick={handlePlayPause}
                    className="min-w-[80px] flex items-center justify-center"
                  >
                    {isPlaying ? (
                      <>
                        <Pause className="w-4 h-4 mr-1.5" />
                        暂停
                      </>
                    ) : (
                      <>
                        <Play className="w-4 h-4 mr-1.5" />
                        播放
                      </>
                    )}
                  </Button>
                  <Button 
                    variant="outline"
                    size="sm"
                    onClick={handleNextPage}
                    disabled={currentPage >= contents.length}
                    className={cn(
                      "transition-opacity",
                      currentPage >= contents.length && "opacity-50"
                    )}
                  >
                    下一页
                  </Button>
                  <Button 
                    variant="outline"
                    size="sm"
                    onClick={handleLastPage}
                    disabled={currentPage >= contents.length}
                    className={cn(
                      "transition-opacity",
                      currentPage >= contents.length && "opacity-50"
                    )}
                  >
                    末页
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* 分隔线 */}
          <div className="border-t border-gray-100" />

          {/* 评论区 */}
          <div className="space-y-6">
            <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
              <MessageSquare className="w-5 h-5" />
              评论区
            </h2>

            {/* 评论输入框 */}
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-gray-100" />
              <div className="flex-1">
                <textarea
                  className="w-full min-h-[100px] p-4 rounded-xl border border-gray-200 focus:border-primary/20 focus:ring-2 focus:ring-primary/20 transition-all resize-none"
                  placeholder="说说你的想法..."
                />
                <div className="flex justify-end mt-2">
                  <Button className="flex items-center gap-2">
                    <MessageSquare className="w-4 h-4" />
                    <span>发表评论</span>
                  </Button>
                </div>
              </div>
            </div>

            {/* 评论列表 */}
            <div className="space-y-6">
              {/* TODO: 从 API 获取评论数据 */}
            </div>
          </div>
        </div>
      </Card>
    </div>
  )
}

export default StoryDetail 